import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";

import ServiceCatalog from "../../pages/ServiceCatalog";
import { getServiceCatalog } from "../../helpers/utils";
import { useSharedProps } from "../../HOC/SharedProps";
import Loader from "../Loading";
import DaariSecrets from "./DaariSecrets";

// Category name reserved for the hard-pinned Daari Secrets tab.
const SECRETS_TAB = "Secrets";

/**
 * Daari = the platform-managed services catalog. Each tab is a category
 * (Database, Storage, Queue, LLM, ...) sourced from the backend
 * `getServiceCatalog` response (category + categoryOrder columns added
 * in coordinator V27). Inside each tab, the existing ServiceCatalog page
 * renders the cards-and-instances UI filtered to that category, so the
 * binding/injection flow stays unchanged.
 *
 * Future sub-products (Vector Stores, Embeddings, Model Registry,
 * Prompts, Agents, ...) become catalog entries on the backend and appear
 * here automatically — no console change required.
 */
export default function DaariView({ currentUser }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { showError } = useSharedProps();

  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    getServiceCatalog(null, (result, err) => {
      setIsLoading(false);

      if (err || !Array.isArray(result)) {
        showError(err || "Failed to load Daari catalog");
        return;
      }

      // Group catalog entries by category. Entries with a null/blank
      // category land in "Other" so a half-deployed backend (V27 columns
      // present but not yet backfilled) still shows something.
      const byCategory = new Map();
      for (const item of result) {
        const cat = item?.category?.trim() || "Other";
        const order = Number.isInteger(item?.categoryOrder)
          ? item.categoryOrder
          : Number.MAX_SAFE_INTEGER;

        if (!byCategory.has(cat)) {
          byCategory.set(cat, { name: cat, order, count: 0 });
        }
        const entry = byCategory.get(cat);
        entry.count += 1;
        entry.order = Math.min(entry.order, order);
      }

      const ordered = Array.from(byCategory.values()).sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.name.localeCompare(b.name);
      });

      // Pin Secrets at the front. If the backend catalog ever grows a real
      // "Secrets" category we still want our hand-rolled tab to win — the
      // Daari Secrets backend (POST /api/v1/daari/secrets/...) is not the
      // generic ServiceCatalog flow.
      const withSecrets = [
        { name: SECRETS_TAB, order: -1, count: 0 },
        ...ordered.filter((c) => c.name !== SECRETS_TAB),
      ];

      setCategories(withSecrets);
      if (withSecrets.length > 0) {
        setActiveTab((prev) => prev || withSecrets[0].name);
      }
    });
  }, [showError]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.name === activeTab) || categories[0],
    [activeTab, categories]
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Daari
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Platform-managed services
          </Typography>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <Loader />
        </Box>
      ) : categories.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          No services available in the catalog.
        </Typography>
      ) : (
        <>
          {/* Category tabs */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: isDark ? "#1e293b" : "divider",
              mb: 2,
            }}
          >
            <Tabs
              value={activeCategory?.name || false}
              onChange={(_, value) => setActiveTab(value)}
              aria-label="Daari service categories"
              variant="scrollable"
              scrollButtons="auto"
            >
              {categories.map((cat) => (
                <Tab key={cat.name} value={cat.name} label={cat.name} />
              ))}
            </Tabs>
          </Box>

          {/* Active category — render the existing ServiceCatalog page
              filtered to this category. Each tab switch remounts so the
              page does its own data fetches; categories are small enough
              that this is fine for now. */}
          {activeCategory && (
            <Box
              key={activeCategory.name}
              role="tabpanel"
              aria-label={activeCategory.name}
            >
              {activeCategory.name === SECRETS_TAB ? (
                <DaariSecrets />
              ) : (
                <ServiceCatalog
                  currentUser={currentUser}
                  categoryFilter={activeCategory.name}
                  embedded
                />
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
